import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, BASE_URL } from '../api';
import { Cantiere, Lotto, DigitalObject, Package } from '../types';
import ActionMenu from '../components/ActionMenu';
import MonitoringModal, { PackageStatus } from '../components/MonitoringModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { useNotification } from '../components/NotificationContext';

const romanToArabic = (s: string): string => {
  if (!s) return s;
  const sClean = s.trim().toUpperCase();
  const romanRegex = /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
  if (!romanRegex.test(sClean) || sClean === '') {
    return s;
  }
  const romanMap: { [key: string]: number } = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let total = 0;
  let prevVal = 0;
  for (let i = sClean.length - 1; i >= 0; i--) {
    const char = sClean[i];
    const val = romanMap[char] || 0;
    if (val >= prevVal) {
      total += val;
    } else {
      total -= val;
    }
    prevVal = val;
  }
  return String(total);
};

interface CantiereBrowserProps {
  cantiere: Cantiere;
  onSelectDigitalObject: (cantiereId: number, objectName: string) => void;
  onBack: () => void;
}

const CantiereBrowser: React.FC<CantiereBrowserProps> = ({ cantiere, onSelectDigitalObject, onBack }) => {
  const { notify } = useNotification();
  const [digitalObjects, setDigitalObjects] = useState<DigitalObject[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [lotti, setLotti] = useState<Lotto[]>([]);
  const [activeTab, setActiveTab] = useState<'objects' | 'packages' | 'lotti' | 'config'>('objects');
  const [message, setMessage] = useState(''); // Local fallback or internal messages
  const [editingObject, setEditingObject] = useState<DigitalObject | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  
  // Package Generation Modal State
  const [isPkgModalOpen, setIsPkgModalOpen] = useState(false);
  const [pkgModalObj, setPkgModalObj] = useState<DigitalObject | null>(null);
  const [pkgModalDirName, setPkgModalDirName] = useState('');
  const [isPkgModalLoading, setIsPkgModalLoading] = useState(false);

  const [exploringPackage, setExploringPackage] = useState<Package | null>(null);
  const [packageContents, setPackageContents] = useState<{ name: string, is_dir: boolean, size: number | null }[]>([]);
  const [isExploreModalOpen, setIsExploreModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [pkgFormData, setPkgFormData] = useState({ name: '', index: 0 });

  const [isXmlModalOpen, setIsXmlModalOpen] = useState(false);
  const [xmlContent, setXmlContent] = useState('');
  const [xmlLoading, setXmlLoading] = useState(false);
  const [xmlFilename, setXmlFilename] = useState('');

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(`cantiere_filters_${cantiere.id}`);
      return saved ? JSON.parse(saved) : {
        directory_name: '',
        lotto: '',
        title: '',
        object_id: '',
        status: ''
      };
    } catch {
      return {
        directory_name: '',
        lotto: '',
        title: '',
        object_id: '',
        status: ''
      };
    }
  });

  const [pkgFilters, setPkgFilters] = useState({
    directory_name: '',
    max_size_mb: ''
  });

  const [objPage, setObjPage] = useState(() => {
    try {
      const saved = localStorage.getItem(`cantiere_objPage_${cantiere.id}`);
      return saved ? parseInt(saved, 10) : 1;
    } catch {
      return 1;
    }
  });

  const [sortField, setSortField] = useState<'directory_name' | 'lotto' | null>(() => {
    try {
      const saved = localStorage.getItem(`cantiere_sortField_${cantiere.id}`);
      return (saved === 'directory_name' || saved === 'lotto') ? saved : null;
    } catch {
      return null;
    }
  });

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    try {
      const saved = localStorage.getItem(`cantiere_sortDirection_${cantiere.id}`);
      return saved === 'desc' ? 'desc' : 'asc';
    } catch {
      return 'asc';
    }
  });

  const [pkgPage, setPkgPage] = useState(1);
  const [stats, setStats] = useState<any>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [prepStatus, setPrepStatus] = useState<any>(null);
  const [showPrepSummary, setShowPrepSummary] = useState(false);
  const [lastSummarySeenAt, setLastSummarySeenAt] = useState<string | null>(null);

  const [labelStatus, setLabelStatus] = useState<any>(null);
  const [labelUploadStatus, setLabelUploadStatus] = useState<any>(null);
  const [showLabelSummary, setShowLabelSummary] = useState(false);
  const [lastLabelSummarySeenAt, setLastLabelSummarySeenAt] = useState<string | null>(null);

  const [downloadLabelsStatus, setDownloadLabelsStatus] = useState<any>(null);
  const [showDownloadLabelsModal, setShowDownloadLabelsModal] = useState(false);

  const [xmlStatus, setXmlStatus] = useState<any>(null);
  const [showXmlSummary, setShowXmlSummary] = useState(false);
  const [lastXmlSummarySeenAt, setLastXmlSummarySeenAt] = useState<string | null>(null);

  const [isMonitoringOpen, setIsMonitoringOpen] = useState(false);
  const [monitoringPkgStatus, setMonitoringPkgStatus] = useState<PackageStatus | null>(null);
  const [isShipping, setIsShipping] = useState<number | null>(null);
  const [isForceSyncing, setIsForceSyncing] = useState(false);
  const [isReconstructing, setIsReconstructing] = useState(false);

  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState(false);
  const [selectedDefaultType, setSelectedDefaultType] = useState('BIB-001');
  const [forceGenerateLabels, setForceGenerateLabels] = useState(false);

  const [batchActionModal, setBatchActionModal] = useState<'exif' | 'xml' | 'packages' | null>(null);
  const [batchScope, setBatchScope] = useState<'all' | 'filtered'>('all');

  // Label Upload States
  const [isLabelUploadModalOpen, setIsLabelUploadModalOpen] = useState(false);
  const [labelUploadFile, setLabelUploadFile] = useState<File | null>(null);
  const [labelUploadId, setLabelUploadId] = useState<string | null>(null);
  const [labelUploadItems, setLabelUploadItems] = useState<any[]>([]);
  const [isLabelUploading, setIsLabelUploading] = useState(false);
  const [labelApplyStatus, setLabelApplyStatus] = useState<{ success: number, errors: number, details: string[] } | null>(null);

  const [isSentinelModalOpen, setIsSentinelModalOpen] = useState(false);
  const [isSentinelUploading, setIsSentinelUploading] = useState(false);
  const sentinelFileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadReport = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiere.id}/report?jwt=${token}`, '_blank');
  };

  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  const [mountTime] = useState(new Date().toISOString());
  const ITEMS_PER_PAGE = 50;

  const [formData, setFormData] = useState({
    creator: cantiere.creator || '',
    ipower1: cantiere.ipower1 || '',
    ipower2: cantiere.ipower2 || '',
    custodian: cantiere.custodian || '',
    email: cantiere.email || '',
    conservative_id: cantiere.conservative_id || '',
    conservative_id_authority: cantiere.conservative_id_authority || '',
    relation_id: cantiere.relation_id || '',
    collection: cantiere.collection || '',
    publisher: cantiere.publisher || '',
    producer: cantiere.producer || '',
    publication_place: cantiere.publication_place || '',
    dpac_code: cantiere.dpac_code || '',
    region_code: cantiere.region_code || '',
  });

  const [objFormData, setObjFormData] = useState<Partial<DigitalObject>>({});


  useEffect(() => {
    // Auto-generate Object ID for ARC lotti
    if (objFormData.lotto_id) {
      const selectedLotto = lotti.find(l => l.id === parseInt(objFormData.lotto_id as any));
      if (selectedLotto && selectedLotto.type.startsWith('ARC-')) {
        const conservativeId = formData.conservative_id || '';
        const logicalId = objFormData.logical_id || '';
        const newObjectId = `${conservativeId}_${logicalId}`;

        if (objFormData.object_id !== newObjectId) {
          setObjFormData(prev => ({ ...prev, object_id: newObjectId }));
        }
      }
    }
  }, [objFormData.lotto_id, objFormData.logical_id, formData.conservative_id, lotti]);

  // Polling for monitoring modal — only depends on isMonitoringOpen and package ID
  // Deliberately NOT including monitoringPkgStatus.is_generating in deps to avoid
  // clearing/recreating the interval on every status update (stale closure issue)
  useEffect(() => {
    if (!isMonitoringOpen || !monitoringPkgStatus?.id) return;
    const pkgId = monitoringPkgStatus.id;
    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/user/package/${pkgId}/status`);
        setMonitoringPkgStatus(data);
        if (!data.is_generating) {
          clearInterval(interval);
          fetchDigitalObjects();
        }
      } catch (error) {
        console.error("Error polling package status:", error);
        clearInterval(interval);
        setMonitoringPkgStatus(prev => prev ? { ...prev, is_generating: false, last_status_message: 'Errore durante il monitoraggio.' } : null);
      }
    }, 1500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonitoringOpen, monitoringPkgStatus?.id]);

  const filteredPackages = React.useMemo(() => {
    return packages.filter(pkg => {
      const matchDir = !pkgFilters.directory_name ||
        (pkg.directory_name || '').toLowerCase().includes(pkgFilters.directory_name.toLowerCase());

      const matchSize = !pkgFilters.max_size_mb ||
        (pkg.zip_size_bytes && pkg.zip_size_bytes < (parseFloat(pkgFilters.max_size_mb) * 1024 * 1024));

      return matchDir && matchSize;
    });
  }, [packages, pkgFilters]);

  const fetchDigitalObjects = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/digital-objects`);
      setDigitalObjects(data.digital_objects);
    } catch (error: any) {
      notify(error.message || 'Errore durante il caricamento degli oggetti.', 'error');
    }
  }, [cantiere.id, notify]);

  const handleForceSync = async () => {
    setIsForceSyncing(true);
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/digital-objects?force=true`);
      setDigitalObjects(data.digital_objects);
      notify('Sincronizzazione completata!', 'success');
      fetchStats();
    } catch (error: any) {
      notify(error.message || 'Errore durante la sincronizzazione.', 'error');
    } finally {
      setIsForceSyncing(false);
    }
  };

  const fetchPackages = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/packages`);
      setPackages(data.packages);
    } catch (error: any) {
      notify(error.message || 'Errore durante il caricamento dei pacchetti.', 'error');
    }
  }, [cantiere.id]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/stats`);
      setStats(data);
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
    }
  }, [cantiere.id]);

  const fetchPrepStatus = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/preparation-status`);
      setPrepStatus(data);
      if (data.status === 'completed' && data.completed_at && data.completed_at !== lastSummarySeenAt) {
        // Only show if it completed AFTER we mounted the component
        if (data.completed_at > mountTime) {
          setShowPrepSummary(true);
        }
        setLastSummarySeenAt(data.completed_at);
      }
    } catch (error: any) {
      console.error('Failed to fetch preparation status:', error);
    }
  }, [cantiere.id, lastSummarySeenAt]);

  const fetchLotti = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/lotti`);
      setLotti(data.lotti);
    } catch (error: any) {
      notify(error.message || 'Errore durante il caricamento dei lotti.', 'error');
    }
  }, [cantiere.id]);

  const handleDownloadErrors = () => {
    if (!prepStatus || !prepStatus.errors.length) return;
    const content = prepStatus.errors.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `errori_preparazione_${cantiere.id}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fetchXmlStatus = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/xml-generation-status`);
      setXmlStatus(data);
      if (data.status === 'completed' && data.completed_at && data.completed_at !== lastXmlSummarySeenAt) {
        if (data.completed_at > mountTime) {
          setShowXmlSummary(true);
        }
        setLastXmlSummarySeenAt(data.completed_at);
      }
    } catch (error: any) {
      console.error('Failed to fetch XML generation status:', error);
    }
  }, [cantiere.id, lastXmlSummarySeenAt, mountTime]);

  const handleDownloadXmlErrors = () => {
    if (!xmlStatus || !xmlStatus.errors.length) return;
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiere.id}/download-xml-errors?jwt=${token}`, '_blank');
  };

  const fetchLabelStatus = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/label-generation-status`);
      setLabelStatus(data);
      if (data.status === 'completed' && data.completed_at && data.completed_at !== lastLabelSummarySeenAt) {
        if (data.completed_at > mountTime) {
          setShowLabelSummary(true);
        }
        setLastLabelSummarySeenAt(data.completed_at);
      }
    } catch (error: any) {
      console.error('Failed to fetch label generation status:', error);
    }
  }, [cantiere.id, lastLabelSummarySeenAt, mountTime]);

  const fetchLabelUploadStatus = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/label-upload-status`);
      setLabelUploadStatus(data);
    } catch (error: any) {
      console.error('Failed to fetch label upload status:', error);
    }
  }, [cantiere.id]);

  const fetchDownloadLabelsStatus = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/download-labels-status`);
      setDownloadLabelsStatus(data);
      return data;
    } catch (error: any) {
      console.error('Failed to fetch download labels status:', error);
      return null;
    }
  }, [cantiere.id]);

  const handleDownloadZipFile = () => {
    const token = localStorage.getItem('access_token');
    const link = document.createElement('a');
    link.href = `${BASE_URL}/user/cantiere/${cantiere.id}/download-labels-zip?jwt=${token}`;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStartDownloadLabels = async () => {
    try {
      notify('Avvio esportazione label...', 'info');
      setDownloadLabelsStatus({ status: 'running', processed: 0, total: 0 });
      setShowDownloadLabelsModal(true);
      
      await api.post(`/user/cantiere/${cantiere.id}/download-labels-start`, {});
      
      let errorCount = 0;
      const poll = async () => {
        const statusData = await fetchDownloadLabelsStatus();
        if (!statusData) {
          errorCount++;
          if (errorCount < 10) {
            setTimeout(poll, 2000);
          } else {
            notify('Connessione al server persa. Esportazione interrotta.', 'error');
            setDownloadLabelsStatus({ 
              status: 'failed', 
              error: 'Impossibile contattare il server per ricevere gli aggiornamenti di stato.' 
            });
          }
          return;
        }
        
        errorCount = 0;
        
        if (statusData.status === 'completed') {
          notify('Esportazione completata! Download in corso...', 'success');
          handleDownloadZipFile();
        } else if (statusData.status === 'running' || statusData.status === 'idle') {
          setTimeout(poll, 1500);
        } else if (statusData.status === 'failed') {
          notify(`Errore durante l'esportazione: ${statusData.error || ''}`, 'error');
        }
      };
      
      setTimeout(poll, 1000);
    } catch (error: any) {
      notify(error.message || 'Errore durante l\'avvio dell\'esportazione.', 'error');
      setShowDownloadLabelsModal(false);
    }
  };

  const handleDownloadLabelErrors = () => {
    if (!labelStatus || !labelStatus.errors.length) return;
    const content = labelStatus.errors.map((e: any) => `Oggetto: ${e.object}\nErrori/Avvisi:\n${e.content}\n${'-'.repeat(40)}`).join('\n\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `errori_label_${cantiere.name}_${cantiere.id}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Setup polling and initial fetch
  useEffect(() => {
    // Initial fetch
    fetchDigitalObjects();
    fetchPackages();
    fetchLotti();
    fetchStats();

    // Setup polling for dynamic lists and stats
    const interval = setInterval(() => {
      fetchStats();
      fetchPrepStatus();
      fetchLabelStatus();
      fetchLabelUploadStatus();
      fetchXmlStatus();
      if (activeTab === 'objects') fetchDigitalObjects();
      else if (activeTab === 'packages') fetchPackages();
    }, 5000);

    return () => clearInterval(interval);
  }, [activeTab, fetchDigitalObjects, fetchPackages, fetchLotti, fetchStats, fetchPrepStatus, fetchLabelStatus, fetchXmlStatus]);

  useEffect(() => {
    localStorage.setItem(`cantiere_filters_${cantiere.id}`, JSON.stringify(filters));
  }, [filters, cantiere.id]);

  useEffect(() => {
    localStorage.setItem(`cantiere_objPage_${cantiere.id}`, objPage.toString());
  }, [objPage, cantiere.id]);

  useEffect(() => {
    if (sortField) {
      localStorage.setItem(`cantiere_sortField_${cantiere.id}`, sortField);
    } else {
      localStorage.removeItem(`cantiere_sortField_${cantiere.id}`);
    }
  }, [sortField, cantiere.id]);

  useEffect(() => {
    localStorage.setItem(`cantiere_sortDirection_${cantiere.id}`, sortDirection);
  }, [sortDirection, cantiere.id]);

  const toggleSort = (field: 'directory_name' | 'lotto') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredObjects = React.useMemo(() => {
    let result = digitalObjects.filter(obj => {
      const lottoName = lotti.find(l => l.id === obj.lotto_id)?.name || '-';
      return (
        obj.directory_name.toLowerCase().includes(filters.directory_name.toLowerCase()) &&
        lottoName.toLowerCase().includes(filters.lotto.toLowerCase()) &&
        (obj.title || '').toLowerCase().includes(filters.title.toLowerCase()) &&
        (obj.object_id || '').toLowerCase().includes(filters.object_id.toLowerCase()) &&
        obj.status.toLowerCase().includes(filters.status.toLowerCase())
      );
    });

    if (sortField) {
      result.sort((a, b) => {
        let valA = '';
        let valB = '';
        if (sortField === 'directory_name') {
          valA = (a.directory_name || '').toLowerCase();
          valB = (b.directory_name || '').toLowerCase();
        } else if (sortField === 'lotto') {
          valA = (lotti.find(l => l.id === a.lotto_id)?.name || '-').toLowerCase();
          valB = (lotti.find(l => l.id === b.lotto_id)?.name || '-').toLowerCase();
        }
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [digitalObjects, filters, lotti, sortField, sortDirection]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put(`/user/cantiere/${cantiere.id}/config`, formData);
      notify('Configurazione aggiornata con successo!', 'success');
    } catch (error: any) {
      notify(error.message || 'Errore durante l\'aggiornamento.', 'error');
    }
  };

  const handleDownloadConfigExcel = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiere.id}/config-excel?jwt=${token}`, '_blank');
  };

  const handleDownloadObjectsExcel = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiere.id}/objects-excel?jwt=${token}`, '_blank');
  };

  const handleDownloadPackagesReportExcel = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiere.id}/packages-report-excel?jwt=${token}`, '_blank');
  };

  const handleEditObject = (obj: DigitalObject) => {
    setEditingObject(obj);
    setObjFormData({ ...obj });
  };

  const handleOpenPkgModal = async (obj: DigitalObject) => {
    setPkgModalObj(obj);
    setIsPkgModalOpen(true);
    setIsPkgModalLoading(true);
    setPkgModalDirName('');
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/object/${obj.directory_name}/suggest-package-name`);
      setPkgModalDirName(data.suggested_name || '');
    } catch (error: any) {
      notify(error.response?.data?.message || "Errore nel recupero del nome pacchetto consigliato", "error");
    } finally {
      setIsPkgModalLoading(false);
    }
  };

  const confirmGeneratePackage = async () => {
    if (!pkgModalObj) return;
    setIsPkgModalLoading(true);
    try {
      const data = await api.post(`/user/cantiere/${cantiere.id}/object/${pkgModalObj.directory_name}/generate-package`, {
        package_name: pkgModalDirName
      });
      setIsPkgModalOpen(false);
      setPkgModalObj(null);
      setPkgModalDirName('');
      if (data.package_id) {
        setMonitoringPkgStatus({ id: data.package_id, is_generating: true, processed_files: 0, total_files: 0, last_status_message: 'Avvio generazione...' });
        setIsMonitoringOpen(true);
      } else {
        notify("Generazione avviata, controlla lo stato tra pochi secondi.", "info");
      }
    } catch (error: any) {
      notify(error.response?.data?.message || "Errore durante l'avvio della generazione", "error");
    } finally {
      setIsPkgModalLoading(false);
    }
  };

  const handleGeneratePrototype = async (obj: DigitalObject) => {
    const lprt_serial = prompt("Inserisci seriale LPRT (es. 001):");
    if (!lprt_serial) return;

    try {
      const data = await api.post(`/user/cantiere/${cantiere.id}/object/${obj.directory_name}/generate-prototype`, { lprt_serial });
      if (data.package_id) {
        setMonitoringPkgStatus({ id: data.package_id, is_generating: true, processed_files: 0, total_files: 0, last_status_message: 'Avvio generazione prototipo...' });
        setIsMonitoringOpen(true);
      } else {
        notify("Generazione prototipo avviata.", "info");
      }
    } catch (error: any) {
      notify(error.response?.data?.message || "Errore durante l'avvio della generazione prototipo", "error");
    }
  };

  const handleObjInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Lotto ID must be a number or null
    const val = name === 'lotto_id' ? (value ? parseInt(value) : null) : value;

    setObjFormData(prev => {
      const newData = { ...prev, [name]: val };

      // Auto-calculate Object ID logic
      const targetLottoId = newData.lotto_id !== undefined ? newData.lotto_id : editingObject?.lotto_id;
      const currentLotto = lotti.find(l => String(l.id) === String(targetLottoId));
      let lotType = currentLotto?.type;

      // Heuristic for Non Lottizzato objects: if periodical fields are filled, assume BIB-002
      if (!lotType && (newData.periodical_issue || newData.periodical_vol)) {
        lotType = 'BIB-002';
      }

      const conservativeId = cantiere.conservative_id || '';
      const logicalId = newData.logical_id || '';

      if (lotType === 'BIB-001') {
        newData.object_id = `${conservativeId}_${logicalId}`;
      } else if (lotType === 'BIB-001M' || lotType === 'BIB-003') {
        const managementId = (newData.management_id || '').replace(/\s+/g, '');
        newData.object_id = `${conservativeId}_${logicalId}-${managementId}`;
      } else if (lotType === 'BIB-008') {
        const managementId = (newData.management_id || '').replace(/\s+/g, '');
        newData.object_id = `${conservativeId}_CNMD${logicalId}-CNMS${managementId}`;
      } else if (lotType === 'BIB-002') {
        const volStartArab = romanToArabic(newData.periodical_vol || '');
        const volEndArab = romanToArabic(newData.periodical_vol_end || '');
        const issueStartArab = romanToArabic(newData.periodical_issue || '');
        const issueEndArab = romanToArabic(newData.periodical_issue_end || '');

        const annataStart = volStartArab.padStart(4, '0') || '0000';
        const annataEnd = volEndArab ? volEndArab.padStart(4, '0') : '';
        const annataPart = annataEnd ? `${annataStart}-${annataEnd}` : annataStart;

        // Handle range in periodical_issue (e.g., 1-5 -> 0001-0005)
        let numeroStart = issueStartArab;
        let dossierNumeroStart = issueStartArab;
        if (numeroStart.includes('-')) {
          const parts = numeroStart.split('-').map(part => part.trim().padStart(4, '0'));
          numeroStart = parts.join('-');
          dossierNumeroStart = parts.join('/');
        } else {
          numeroStart = numeroStart.padStart(4, '0') || '0000';
          dossierNumeroStart = numeroStart;
        }
        const numeroEnd = issueEndArab ? issueEndArab.padStart(4, '0') : '';
        const numeroPart = numeroEnd ? `${numeroStart}-${numeroEnd}` : numeroStart;

        let suffix = '';
        if (newData.periodical_type === 'Supplemento') suffix = '-sup';
        else if (newData.periodical_type === 'Indice') suffix = '-ind';

        newData.object_id = `${conservativeId}_${logicalId}-${annataPart}-${numeroPart}${suffix}`;

        // Dossier ID SICI format calculation
        const yearStart = (newData.periodical_year || '').trim();
        const yearEnd = (newData.periodical_year_end || '').trim();
        const monthStart = (newData.periodical_month || '').trim().padStart(2, '0') || '00';
        const monthEnd = (newData.periodical_month_end || '').trim().padStart(2, '0') || '00';
        const dayStart = (newData.periodical_day || '').trim().padStart(2, '0') || '00';
        
        const hasEnds = !!(newData.periodical_year_end || newData.periodical_month_end || newData.periodical_vol_end || newData.periodical_issue_end);
        
        // 1. DataFormattata
        let dataFormattata = '';
        if (hasEnds) {
          const yearEndVal = yearEnd ? yearEnd : yearStart;
          if (yearStart === yearEndVal) {
            dataFormattata = `${yearStart.padStart(4, '0')}${monthStart}/${monthEnd}`;
          } else {
            dataFormattata = `${yearStart.padStart(4, '0')}${monthStart}/${yearEndVal.padStart(4, '0')}${monthEnd}`;
          }
        } else {
          dataFormattata = `${yearStart.padStart(4, '0')}${monthStart}${dayStart}`;
        }
        
        // 2. VolumePart
        let volumePart = '';
        const yStartNum = parseInt(yearStart, 10);
        const yEndNum = parseInt(yearEnd, 10);
        const hasMultipleYears = !isNaN(yStartNum) && !isNaN(yEndNum) && yEndNum > yStartNum;

        if (hasMultipleYears) {
          volumePart = '0000';
        } else if (hasEnds) {
          if (volStartArab === volEndArab || !volEndArab) {
            volumePart = volStartArab.padStart(4, '0') || '0000';
          } else {
            volumePart = `${volStartArab.padStart(4, '0')}/${volEndArab.padStart(4, '0')}`;
          }
        } else {
          volumePart = annataEnd ? `${annataStart}/${annataEnd}` : annataStart;
        }

        // 3. NumeroPart
        let numeroPartSici = '';
        if (hasEnds) {
          const yearEndVal = yearEnd ? yearEnd : yearStart;
          if (yearStart === yearEndVal) {
            if (issueStartArab !== issueEndArab && issueEndArab) {
              numeroPartSici = `${issueStartArab.padStart(4, '0')}/${issueEndArab.padStart(4, '0')}`;
            } else {
              numeroPartSici = dossierNumeroStart;
            }
          } else {
            numeroPartSici = '0000';
          }
        } else {
          numeroPartSici = numeroEnd ? `${dossierNumeroStart}/${numeroEnd}` : dossierNumeroStart;
        }

        let extraSuffix = '';
        if (newData.periodical_type === 'Supplemento') extraSuffix = '+';
        else if (newData.periodical_type === 'Indice') extraSuffix = '*';

        newData.dossier_id = `(${dataFormattata})${volumePart}:${numeroPartSici}:0000${extraSuffix}`;

        // Periodico part-detail-number calculation
        const volStr = (newData.periodical_vol || '').trim();
        const volEndStr = (newData.periodical_vol_end || '').trim();
        const isVolEmpty = !volStr || volStr === '0' || volStr === '0000' || volStr === '00000';
        const isVolEndEmpty = !volEndStr || volEndStr === '0' || volEndStr === '0000' || volEndStr === '00000';
        
        let detailAnnata = '';
        if (isVolEmpty && isVolEndEmpty) {
          detailAnnata = '';
        } else if (!isVolEmpty && isVolEndEmpty) {
          detailAnnata = volStr;
        } else if (isVolEmpty && !isVolEndEmpty) {
          detailAnnata = volEndStr;
        } else {
          if (volStr === volEndStr) {
            detailAnnata = volStr;
          } else {
            detailAnnata = `${volStr}-${volEndStr}`;
          }
        }

        const issueStr = (newData.periodical_issue || '').trim();
        const issueEndStr = (newData.periodical_issue_end || '').trim();
        const isIssueEmpty = !issueStr || issueStr === '0' || issueStr === '0000' || issueStr === '00000';
        const isIssueEndEmpty = !issueEndStr || issueEndStr === '0' || issueEndStr === '0000' || issueEndStr === '00000';

        let detailNumero = '';
        if (isIssueEmpty && isIssueEndEmpty) {
          detailNumero = '';
        } else if (!isIssueEmpty && isIssueEndEmpty) {
          let startNum = issueStr;
          if (startNum.includes('-')) {
            startNum = startNum.split('-').map(part => part.trim()).join(', ');
          }
          detailNumero = startNum;
        } else if (isIssueEmpty && !isIssueEndEmpty) {
          detailNumero = issueEndStr;
        } else {
          let startNum = issueStr;
          if (startNum.includes('-')) {
            startNum = startNum.split('-').map(part => part.trim()).join(', ');
          }
          if (issueStr === issueEndStr) {
            detailNumero = startNum;
          } else {
            detailNumero = `${startNum}-${issueEndStr}`;
          }
        }

        const parts: string[] = [];
        if (detailAnnata) parts.push(`A. ${detailAnnata}`);
        if (detailNumero) parts.push(`n. ${detailNumero}`);

        let partDetailStr = parts.join(', ');

        const detailSuffix = newData.periodical_type === 'Supplemento' ? 'supplemento' : (newData.periodical_type === 'Indice' ? 'indice' : '');
        if (detailSuffix) {
          if (partDetailStr) {
            partDetailStr = `${partDetailStr}, ${detailSuffix}`;
          } else {
            partDetailStr = detailSuffix;
          }
        }

        newData.periodical_part_detail_number = partDetailStr;

        // Periodico part-data calculation: <mese trascritto> <anno>
        const monthMap: { [key: string]: string } = {
          '01': 'gen.', '02': 'feb.', '03': 'mar.', '04': 'apr.', '05': 'mag.', '06': 'giu.',
          '07': 'lug.', '08': 'ago.', '09': 'set.', '10': 'ott.', '11': 'nov.', '12': 'dic.'
        };
        const rawMonthStart = newData.periodical_month || '00';
        const translatedMonthsStart = rawMonthStart.split('/').map(m => monthMap[m.padStart(2, '0')] || m);
        const startDateStr = `${translatedMonthsStart.join('-')} ${newData.periodical_year || '0000'}`;

        if (newData.periodical_year_end || newData.periodical_month_end) {
          const rawMonthEnd = newData.periodical_month_end || '00';
          const translatedMonthsEnd = rawMonthEnd.split('/').map(m => monthMap[m.padStart(2, '0')] || m);
          const endDateStr = `${translatedMonthsEnd.join('-')} ${newData.periodical_year_end || '0000'}`;
          newData.periodical_part_data = `${startDateStr} - ${endDateStr}`;
        } else {
          newData.periodical_part_data = startDateStr;
        }
      }

      return newData;
    });
  };

  const handleSaveObject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingObject) return;
    try {
      await api.put(`/user/digital-object/${editingObject.id}`, objFormData);
      notify('Oggetto digitale aggiornato!', 'success');
      setEditingObject(null);
      fetchDigitalObjects();
    } catch (error: any) {
      notify(error.message || 'Errore salvataggio oggetto.', 'error');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImportFile(file);
      setImportLoading(true);
      setImportMessage('');

      const formData = new FormData();
      formData.append('file', file);

      try {
        const data = await api.post(`/user/cantiere/${cantiere.id}/import-excel/preview`, formData);
        setImportPreview(data.preview);
      } catch (error: any) {
        setImportMessage(error.message || 'Errore durante la preview.');
      } finally {
        setImportLoading(false);
      }
    }
  };

  const handleGenerateXml = () => {
    setBatchScope('all');
    setBatchActionModal('xml');
  };

  const confirmGenerateXml = async () => {
    try {
      setBatchActionModal(null);
      notify('Avvio generazione XML...', 'info');
      setShowXmlSummary(true);
      const body: any = {};
      if (batchScope === 'filtered') {
        body.object_names = filteredObjects.map(o => o.directory_name);
      }
      const data = await api.post(`/user/cantiere/${cantiere.id}/generate-xml-all`, body);
      notify(data.message || 'Generazione XML avviata in background.', 'success');
      fetchXmlStatus();
    } catch (error: any) {
      notify(error.message || 'Errore durante la generazione XML.', 'error');
      setShowXmlSummary(false);
    }
  };

  const handleLabelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // Reset to allow re-uploading the same file

    setLabelUploadFile(file);
    setIsLabelUploading(true);
    setLabelApplyStatus(null);
    setLabelUploadItems([]);
    setIsLabelUploadModalOpen(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await api.post(`/user/cantiere/${cantiere.id}/upload-labels-zip`, formData);
      setLabelUploadId(resp.upload_id);
      setLabelUploadItems(resp.items);
    } catch (err: any) {
      notify(err.message || 'Errore durante il caricamento dello ZIP.', 'error');
      setIsLabelUploadModalOpen(false);
    } finally {
      setIsLabelUploading(false);
    }
  };

  const handleApplyLabels = async () => {
    if (!labelUploadId) return;

    setIsLabelUploading(true);
    setLabelApplyStatus(null);
    try {
      const itemsToApply = labelUploadItems.filter(it => it.status === 'found');
      await api.post(`/user/cantiere/${cantiere.id}/apply-uploaded-labels`, {
        upload_id: labelUploadId,
        items: itemsToApply
      });
      notify(`Applicazione avviata in background.`, 'info');
      // No longer setting labelApplyStatus here, it will be handled by polling
    } catch (err: any) {
      notify(err.message || "Errore durante l'avvio dell'applicazione.", 'error');
      setIsLabelUploading(false);
    }
  };

  const handleCloseLabelUpload = () => {
    setIsLabelUploadModalOpen(false);
    setLabelUploadFile(null);
    setLabelUploadId(null);
    setLabelUploadItems([]);
    setLabelApplyStatus(null);
    setLabelUploadStatus(null);
  };

  const handleCommitImport = async () => {
    if (!importFile) return;
    setImportLoading(true);

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const data = await api.post(`/user/cantiere/${cantiere.id}/import-excel/commit`, formData);
      setMessage(data.message);
      setIsImportModalOpen(false);
      setImportFile(null);
      setImportPreview([]);
      fetchDigitalObjects();
    } catch (error: any) {
      setImportMessage(error.message || 'Errore durante l\'importazione.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleSyncMetadata = async () => {
    try {
      notify('Avvio sincronizzazione metadati...', 'info');
      const data = await api.post(`/user/cantiere/${cantiere.id}/sync-metadata`, {});
      notify(data.message, 'success');
      fetchDigitalObjects();
      fetchStats();
    } catch (error: any) {
      notify(error.message || 'Errore durante la sincronizzazione.', 'error');
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/template/oggetti_digitali?jwt=${token}`, '_blank');
  };

  const handleSyncPackages = async () => {
    try {
      const data = await api.post(`/user/cantiere/${cantiere.id}/packages/sync`, {});
      setMessage(data.message);
      fetchPackages();
    } catch (error: any) {
      setMessage(error.message || 'Errore durante la sincronizzazione.');
    }
  };

  const handleReconstructPackages = () => {
    setConfirmModalConfig({
      isOpen: true,
      title: 'Ricostruisci Database Pacchetti',
      message: 'Questo processo eliminerà tutti i pacchetti correnti nel database per questo cantiere e li ricostruirà da zero eseguendo una scansione fisica dei file sul server (Origin vs Target) e rigenerando i relativi file Excel. Sei sicuro di voler procedere?',
      onConfirm: async () => {
        setIsReconstructing(true);
        try {
          notify('Avvio ricostruzione database...', 'info');
          const data = await api.post(`/user/cantiere/${cantiere.id}/packages/reconstruct`, {});
          notify(data.message || 'Ricostruzione completata con successo!', 'success');
          fetchPackages();
          fetchStats();
        } catch (error: any) {
          const errMsg = error.response?.data?.message || error.message || 'Errore durante la ricostruzione dei pacchetti.';
          notify(errMsg, 'error');
        } finally {
          setIsReconstructing(false);
          setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleExplorePackage = async (pkg: Package) => {
    setExploringPackage(pkg);
    setIsExploreModalOpen(true);
    setPackageContents([]);
    try {
      const data = await api.get(`/user/package/${pkg.id}/contents`);
      setPackageContents(data.contents);
    } catch (error: any) {
      setMessage(error.message || 'Errore durante il caricamento dei contenuti.');
    }
  };

  const handleEditPackage = (pkg: Package) => {
    setEditingPackage(pkg);
    setPkgFormData({ name: pkg.name, index: pkg.index });
  };

  const handleSavePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPackage) return;
    try {
      const result = await api.put(`/user/package/${editingPackage.id}`, pkgFormData);
      const msg = result.new_name && result.new_name !== editingPackage.name
        ? `Pacchetto rinominato in "${result.new_name}" con indice ${result.new_index}.`
        : 'Pacchetto aggiornato!';
      notify(msg, 'success');
      setEditingPackage(null);
      fetchPackages();
    } catch (error: any) {
      // 409 means the index is already taken by another package
      const serverMsg = error.response?.data?.message || error.message;
      notify(serverMsg || 'Errore salvataggio pacchetto.', 'error');
    }
  };


  const handleDeletePackage = async (pkg: Package) => {
    setConfirmModalConfig({
      isOpen: true,
      title: 'Elimina Pacchetto',
      message: `Sei sicuro di voler eliminare il pacchetto ${pkg.name}? Questa azione rimuoverà anche la cartella fisica.`,
      onConfirm: async () => {
        try {
          await api.delete(`/user/package/${pkg.id}`);
          notify('Pacchetto eliminato.', 'success');
          fetchPackages();
        } catch (error: any) {
          notify(error.message || 'Errore eliminazione pacchetto.', 'error');
        } finally {
          setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleViewXml = async (pkg: Package) => {
    setIsXmlModalOpen(true);
    setXmlLoading(true);
    setXmlContent('');
    setXmlFilename('');
    try {
      const data = await api.get(`/user/package/${pkg.id}/xml`);
      setXmlContent(data.content);
      setXmlFilename(data.filename);
    } catch (error: any) {
      setXmlContent(error.message || 'Errore durante il caricamento del file XML.');
    } finally {
      setXmlLoading(false);
    }
  };

  const handleDownloadPackage = (pkg: Package) => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/package/${pkg.id}/download?jwt=${token}`, '_blank');
  };

  const handleResetTransfer = async (pkg: Package) => {
    setConfirmModalConfig({
      isOpen: true,
      title: 'Reset Trasferimento',
      message: `Vuoi resettare lo stato di trasferimento per il pacchetto ${pkg.name}?`,
      onConfirm: async () => {
        try {
          await api.post(`/user/package/${pkg.id}/reset-transfer`, {});
          notify('Stato trasferimento resettato.', 'success');
          fetchPackages();
        } catch (error: any) {
          notify(error.message || 'Errore durante il reset del trasferimento.', 'error');
        } finally {
          setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleShipPackage = async (pkg: Package) => {
    setConfirmModalConfig({
      isOpen: true,
      title: 'Invia a DPAC',
      message: `Sei sicuro di voler inviare il pacchetto ${pkg.name} a DPAC tramite il client "${pkg.dpac_client_name}"?`,

      onConfirm: async () => {
        setIsShipping(pkg.id);
        try {
          await api.post(`/user/package/${pkg.id}/ship`, {});
          notify('Caricamento avviato in background. La riga diventerà verde al termine.', 'success');
          fetchPackages();
        } catch (error: any) {
          notify(error.message || "Errore durante l'invio del pacchetto.", 'error');
        } finally {
          setIsShipping(null);
          setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleCopyLink = (pkgName: string) => {
    const apiBase = BASE_URL.startsWith('http') ? BASE_URL : window.location.origin;
    const fullUrl = `${apiBase}/user/public/package/${pkgName}/download`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      notify("Link di download pubblico copiato negli appunti", 'success');
    }).catch(err => {
      console.error('Errore durante la copia del link: ', err);
      alert("Errore durante la copia del link");
    });
  };

  const isCantiereComplete = digitalObjects.length > 0 && digitalObjects.every(obj =>
    obj.status === 'Completo'
  );

  const handleGenerateExifAll = () => {
    setBatchScope('all');
    setBatchActionModal('exif');
  };

  const confirmGenerateExif = async () => {
    try {
      setBatchActionModal(null);
      notify('Avvio generazione EXIF...', 'info');
      setShowPrepSummary(true); // Open modal immediately
      const body: any = {};
      if (batchScope === 'filtered') {
        body.object_names = filteredObjects.map(o => o.directory_name);
      }
      const data = await api.post(`/user/cantiere/${cantiere.id}/generate-exif`, body);
      notify(data.message, 'success');
      fetchStats();
      fetchDigitalObjects();
    } catch (error: any) {
      notify(error.message || 'Errore durante la generazione EXIF.', 'error');
      setShowPrepSummary(false);
    }
  };




  const handleGenerateLabels = () => {
    setBatchScope('all');
    setShowTypeSelectionModal(true);
  };

  const confirmGenerateLabels = async (defaultType: string | null) => {
    try {
      setShowTypeSelectionModal(false);
      notify('Avvio generazione automatica label...', 'info');
      setShowLabelSummary(true); // Open status modal immediately
      const body: any = {};
      if (defaultType) body.default_type = defaultType;
      if (forceGenerateLabels) body.force = true;
      if (batchScope === 'filtered') {
        body.object_names = filteredObjects.map(o => o.directory_name);
      }
      const data = await api.post(`/user/cantiere/${cantiere.id}/generate-labels`, body);
      notify(data.message, 'success');
      fetchLabelStatus();
    } catch (error: any) {
      notify(error.message || 'Errore durante la generazione delle label.', 'error');
      setShowLabelSummary(false); // Hide on error if needed, or keep to show error?
    }
  };

  /* Batch Package Generation Logic */
  const [pkgGenStatus, setPkgGenStatus] = useState<any>(null);
  const [showPkgGenSummary, setShowPkgGenSummary] = useState(false);
  const [lastPkgGenSummarySeenAt, setLastPkgGenSummarySeenAt] = useState<string | null>(null);

  const fetchPkgGenStatus = useCallback(async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiere.id}/package-generation-status`);
      setPkgGenStatus(data);
      if (data.status === 'completed' && data.completed_at && data.completed_at !== lastPkgGenSummarySeenAt) {
        if (data.completed_at > mountTime) {
          setShowPkgGenSummary(true);
        }
        setLastPkgGenSummarySeenAt(data.completed_at);
      }
    } catch (error: any) {
      console.error('Failed to fetch package generation status:', error);
    }
  }, [cantiere.id, lastPkgGenSummarySeenAt, mountTime]);

  const handleSentinelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setIsSentinelUploading(true);
      const data = await api.post(`/user/cantiere/${cantiere.id}/upload-sentinel-log`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      notify(data.message, 'success');
      fetchPackages();
      fetchStats();
      setIsSentinelModalOpen(false);
    } catch (error: any) {
      notify(error.message || 'Errore durante l\'importazione del log Sentinel', 'error');
    } finally {
      setIsSentinelUploading(false);
      if (sentinelFileInputRef.current) sentinelFileInputRef.current.value = '';
    }
  };

  const handleGeneratePackagesBatch = () => {
    setBatchScope('all');
    setBatchActionModal('packages');
  };

  const confirmGeneratePackages = async () => {
    try {
      setBatchActionModal(null);
      notify('Avvio generazione pacchetti...', 'info');
      setShowPkgGenSummary(true); // Open modal immediately
      const body: any = {};
      if (batchScope === 'filtered') {
        body.object_names = filteredObjects.map(o => o.directory_name);
      }
      const data = await api.post(`/user/cantiere/${cantiere.id}/generate-packages`, body);
      notify(data.message, 'success');
      fetchPkgGenStatus();
    } catch (error: any) {
      notify(error.message || 'Errore durante l\'avvio della generazione pacchetti.', 'error');
      setShowPkgGenSummary(false);
    }
  };

  // Add to polling
  useEffect(() => {
    fetchDigitalObjects();
    fetchPackages();
    fetchLotti();
    fetchStats();
    fetchPkgGenStatus();

    const interval = setInterval(() => {
      fetchStats();
      fetchPrepStatus();
      fetchLabelStatus();
      fetchXmlStatus();
      fetchPkgGenStatus();
      if (activeTab === 'objects') fetchDigitalObjects();
      else if (activeTab === 'packages') fetchPackages();
    }, 5000);

    return () => clearInterval(interval);
  }, [activeTab, fetchDigitalObjects, fetchPackages, fetchLotti, fetchStats, fetchPrepStatus, fetchLabelStatus, fetchXmlStatus, fetchPkgGenStatus]);

  // Render
  return (
    <div className="container">
      {/* ... header ... */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button className="btn btn-text" onClick={onBack}>← Dashboard</button>
        <h2 style={{ margin: 0 }}>{cantiere.dpac_code}: {cantiere.name}</h2>
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleForceSync}
          disabled={isForceSyncing}
          title="Sincronizza ora con il filesystem"
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <i className={`fas fa-sync-alt ${isForceSyncing ? 'fa-spin' : ''}`}></i>
          {isForceSyncing ? 'Sincronizzazione...' : ''}
        </button>
      </div>

      {cantiere.can_edit && (
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '16px',
          padding: '12px',
          background: '#f5f5f5',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          flexWrap: 'wrap'
        }}>
          <button
            className="btn btn-primary"
            onClick={handleGenerateExifAll}
          >
            GENERA EXIF
          </button>
          <button
            className="btn btn-primary"
            onClick={() => document.getElementById('label-zip-input')?.click()}
            disabled={isLabelUploading}
            style={{ backgroundColor: '#ff9800' }}
          >
            {isLabelUploading ? 'CARICAMENTO...' : 'CARICA LABEL'}
          </button>
          <input
            id="label-zip-input"
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={handleLabelUpload}
          />
          <button
            className="btn btn-primary"
            onClick={handleGenerateLabels}
            disabled={labelStatus?.status === 'running'}
          >
            {labelStatus?.status === 'running' ? `LABELING... (${labelStatus.processed}/${labelStatus.total})` : 'GENERA LABEL'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleStartDownloadLabels}
            disabled={downloadLabelsStatus?.status === 'running'}
            style={{ backgroundColor: '#00bcd4' }}
          >
            {downloadLabelsStatus?.status === 'running' ? 'SCARICAMENTO...' : 'SCARICA LABEL'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGenerateXml}
            disabled={xmlStatus?.status === 'running'}
          >
            {xmlStatus?.status === 'running' ? `XML... (${xmlStatus.processed}/${xmlStatus.total})` : 'GENERA XML'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGeneratePackagesBatch}
            disabled={pkgGenStatus?.status === 'running'}
            style={{ backgroundColor: '#673ab7' }} // Distinct color
          >
            {pkgGenStatus?.status === 'running' ? `PACCHETTI... (${pkgGenStatus.processed}/${pkgGenStatus.total})` : 'GENERA PACCHETTI'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSyncMetadata}
            style={{ backgroundColor: '#2e7d32' }} // Green color
          >
            SYNC METADATI
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadReport}>
            <i className="fas fa-chart-line"></i>
            STATISTICHE
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
          <span style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Oggetti</span>
          <span style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{stats?.total_objects ?? '...'}</span>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px', borderLeft: '4px solid #673ab7' }}>
          <span style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Pacchetti</span>
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', lineHeight: '1.2' }}>{stats?.total_packages ?? '0'}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '0.75rem', color: '#666', marginTop: '4px', flexWrap: 'wrap' }}>
              <span>{stats?.total_prototypes ?? '0'} prot.</span>
              <span style={{ color: '#2196f3' }}>{stats?.total_sent ?? '0'} inv.</span>
              <span style={{ color: '#4caf50' }}>{stats?.total_archived ?? '0'} arch.</span>
            </div>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
          <span style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>File Digitali (TIF)</span>
          <span style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{stats?.total_tiff ?? '...'}</span>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
          <span style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Derivati JPEG</span>
          <span style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{stats?.total_jpeg ?? '...'}</span>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModalConfig.isOpen}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        onConfirm={confirmModalConfig.onConfirm}
        onCancel={() => setConfirmModalConfig(prev => ({ ...prev, isOpen: false }))}
        isLoading={isShipping !== null || isReconstructing}
      />

      <nav style={{ borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: '24px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        <button
          className="btn btn-text"
          onClick={() => { setActiveTab('objects'); setEditingObject(null); }}
          style={{ borderBottom: activeTab === 'objects' ? '2px solid var(--primary-color)' : 'none', borderRadius: 0 }}
        >
          OGGETTI DIGITALI
        </button>
        <button
          className="btn btn-text"
          onClick={() => setActiveTab('packages')}
          style={{ borderBottom: activeTab === 'packages' ? '2px solid var(--primary-color)' : 'none', borderRadius: 0 }}
        >
          PACCHETTI
        </button>
        <button
          className="btn btn-text"
          onClick={() => setActiveTab('lotti')}
          style={{ borderBottom: activeTab === 'lotti' ? '2px solid var(--primary-color)' : 'none', borderRadius: 0 }}
        >
          LOTTI
        </button>
        <button
          className="btn btn-text"
          onClick={() => setActiveTab('config')}
          style={{ borderBottom: activeTab === 'config' ? '2px solid var(--primary-color)' : 'none', borderRadius: 0 }}
        >
          CONFIGURAZIONE
        </button>
      </nav>

      {/* Package Generation Summary Modal */}
      {
        showPkgGenSummary && pkgGenStatus && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
              <h2>Stato Generazione Pacchetti</h2>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong>Status:</strong>
                  <span style={{
                    color: pkgGenStatus.status === 'completed' ? 'green' :
                      pkgGenStatus.status === 'running' ? 'blue' : 'black',
                    fontWeight: 'bold'
                  }}>
                    {pkgGenStatus.status.toUpperCase()}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong>Progresso:</strong>
                  <span>{pkgGenStatus.processed} / {pkgGenStatus.total}</span>
                </div>
                {pkgGenStatus.completed_at && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <strong>Completato il:</strong>
                    <span>{new Date(pkgGenStatus.completed_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {pkgGenStatus.total > 0 && (
                <div style={{ width: '100%', height: '10px', background: '#eee', borderRadius: '5px', marginBottom: '20px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(pkgGenStatus.processed / pkgGenStatus.total) * 100}%`,
                    height: '100%',
                    background: pkgGenStatus.status === 'completed' ? '#4caf50' : '#2196f3',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              )}

              {pkgGenStatus.errors && pkgGenStatus.errors.length > 0 && (
                <div style={{ marginTop: '20px', padding: '10px', background: '#ffebee', borderRadius: '4px', border: '1px solid #ffcdd2' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#c62828' }}>Errori ({pkgGenStatus.errors.length})</h4>
                  <ul style={{ maxHeight: '150px', overflowY: 'auto', paddingLeft: '20px', margin: 0 }}>
                    {pkgGenStatus.errors.map((err: string, i: number) => (
                      <li key={i} style={{ fontSize: '0.9rem', marginBottom: '4px' }}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button className="btn btn-primary" onClick={() => setShowPkgGenSummary(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        )
      }

      {/* Legacy local message display removed */}

      {
        activeTab === 'objects' && (
          <div className="card">
            {!editingObject ? (
              <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: 0 }}>Lista Oggetti Digitali</h3>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                    Totale record: <strong>{filteredObjects.length}</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="btn btn-primary" onClick={handleDownloadObjectsExcel}>SCARICA EXCEL</button>
                  {cantiere.can_edit && <button className="btn btn-secondary" onClick={() => setIsImportModalOpen(true)}>CARICA DA EXCEL</button>}
                </div>
              </div>
              <table className="table-responsive">
                <thead>
                  <tr>
                    <th className="desktop-only">Anteprima</th>
                    <th className="col-obj-name" onClick={() => toggleSort('directory_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <span className="desktop-only">Directory Name {sortField === 'directory_name' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                      <span className="mobile-only">Oggetti digitali {sortField === 'directory_name' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </th>
                    <th className="desktop-only" onClick={() => toggleSort('lotto')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      ID Lotto {sortField === 'lotto' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                    </th>
                      <th className="desktop-only">Titolo</th>
                      <th className="desktop-only">Object ID</th>
                      <th>Stato</th>
                      <th>Azioni</th>
                    </tr>
                    <tr>
                      <th className="desktop-only"></th>
                      <th className="col-obj-name">
                        <input
                          type="text"
                          placeholder="Cerca..."
                          style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                          value={filters.directory_name}
                          onChange={(e) => { setFilters({ ...filters, directory_name: e.target.value }); setObjPage(1); }}
                        />
                      </th>
                      <th className="desktop-only">
                        <input
                          type="text"
                          placeholder="Cerca..."
                          style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                          value={filters.lotto}
                          onChange={(e) => { setFilters({ ...filters, lotto: e.target.value }); setObjPage(1); }}
                        />
                      </th>
                      <th className="desktop-only">
                        <input
                          type="text"
                          placeholder="Cerca..."
                          style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                          value={filters.title}
                          onChange={(e) => { setFilters({ ...filters, title: e.target.value }); setObjPage(1); }}
                        />
                      </th>
                      <th className="desktop-only">
                        <input
                          type="text"
                          placeholder="Cerca..."
                          style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                          value={filters.object_id}
                          onChange={(e) => { setFilters({ ...filters, object_id: e.target.value }); setObjPage(1); }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          placeholder="Cerca..."
                          style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                          value={filters.status}
                          onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setObjPage(1); }}
                        />
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredObjects.slice((objPage - 1) * ITEMS_PER_PAGE, objPage * ITEMS_PER_PAGE).map((obj) => (
                      <tr key={obj.id} style={{ backgroundColor: (obj.is_new && !obj.lotto_id) ? '#fff9c4' : 'inherit' }}>
                        <td className="desktop-only">
                          {obj.thumbnail ? (
                            <a
                              href={`${BASE_URL}/user/cantiere/${cantiere.id}/object/${encodeURIComponent(obj.directory_name)}/file/${encodeURIComponent(obj.thumbnail)}?jwt=${localStorage.getItem('access_token')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={`${BASE_URL}/user/cantiere/${cantiere.id}/object/${encodeURIComponent(obj.directory_name)}/file/${encodeURIComponent(obj.thumbnail)}?jwt=${localStorage.getItem('access_token')}`}
                                alt="thumb"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            </a>
                          ) : (
                            <div style={{ width: '50px', height: '50px', background: '#eee', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '0.7rem' }}>N/A</div>
                          )}
                        </td>
                        <td className="col-obj-name"><strong>{obj.directory_name}</strong></td>
                        <td className="desktop-only">{lotti.find(l => l.id === obj.lotto_id)?.name || '-'}</td>
                        <td className="desktop-only">{obj.title || '(non compilato)'}</td>
                        <td className="desktop-only">{obj.object_id || '-'}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            background:
                              obj.status === 'Collaudato' ? '#e8f5e9' :
                                obj.status === 'Completo' ? '#f1f8e9' :
                                  obj.status === 'Errore Mets-Ecomic' ? '#ffebee' :
                                    obj.status === 'In lavorazione' ? '#fff3e0' :
                                      obj.status === 'Non Lottizzato' ? '#fce4ec' :
                                        obj.status === 'Pacchettizzato' ? '#e3f2fd' : '#f5f5f5',
                            color:
                              obj.status === 'Collaudato' ? '#2e7d32' :
                                obj.status === 'Completo' ? '#689f38' :
                                  obj.status === 'Errore Mets-Ecomic' ? '#c62828' :
                                    obj.status === 'In lavorazione' ? '#ef6c00' :
                                      obj.status === 'Non Lottizzato' ? '#880e4f' :
                                        obj.status === 'Pacchettizzato' ? '#1565c0' : '#757575'
                          }}>
                            {obj.status || 'Draft'}
                          </span>
                        </td>
                        <td>
                          <ActionMenu actions={
                            cantiere.can_edit ? [
                              { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => handleEditObject(obj) },
                              { label: 'Esplora File', icon: 'fas fa-magnifying-glass', onClick: () => onSelectDigitalObject(cantiere.id, obj.directory_name) },
                              { label: 'Genera Pacchetto', icon: 'fas fa-box-archive', onClick: () => handleOpenPkgModal(obj) },
                              { label: 'Genera Prototipo', icon: 'fas fa-flask', onClick: () => handleGeneratePrototype(obj) },
                            ] : [
                              { label: 'Esplora File', icon: 'fas fa-magnifying-glass', onClick: () => onSelectDigitalObject(cantiere.id, obj.directory_name) },
                            ]
                          } />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredObjects.length > ITEMS_PER_PAGE && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center', marginTop: '20px' }}>
                    <button
                      className="btn btn-secondary"
                      disabled={objPage === 1}
                      onClick={() => setObjPage(1)}
                    >
                      Prima
                    </button>
                    <button
                      className="btn btn-secondary"
                      disabled={objPage === 1}
                      onClick={() => setObjPage(objPage - 1)}
                    >
                      Prev
                    </button>
                    <span>Pagina {objPage} di {Math.ceil(filteredObjects.length / ITEMS_PER_PAGE)}</span>
                    <button
                      className="btn btn-secondary"
                      disabled={objPage === Math.ceil(filteredObjects.length / ITEMS_PER_PAGE)}
                      onClick={() => setObjPage(objPage + 1)}
                    >
                      Next
                    </button>
                    <button
                      className="btn btn-secondary"
                      disabled={objPage === Math.ceil(filteredObjects.length / ITEMS_PER_PAGE)}
                      onClick={() => setObjPage(Math.ceil(filteredObjects.length / ITEMS_PER_PAGE))}
                    >
                      Ultima
                    </button>
                  </div>
                )}
                {filteredObjects.length === 0 && (
                  <p style={{ textAlign: 'center', padding: '20px', color: 'rgba(0,0,0,0.4)' }}>
                    {digitalObjects.length === 0 ? "Nessun oggetto trovato." : "Nessun oggetto corrisponde ai filtri."}
                  </p>
                )}
              </>
            ) : (
              <>
                <h3>Modifica Oggetto: {editingObject.directory_name}</h3>
                <form onSubmit={handleSaveObject}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="form-group">
                      <label>Lotto Correlato</label>
                      <select name="lotto_id" value={objFormData.lotto_id || ''} onChange={handleObjInputChange}>
                        <option value="">Seleziona Lotto</option>
                        {lotti.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Object ID</label>
                      <input
                        type="text"
                        name="object_id"
                        value={objFormData.object_id || ''}
                        onChange={handleObjInputChange}
                        disabled={
                          lotti.find(l => l.id === objFormData.lotto_id)?.type === 'BIB-001' ||
                          lotti.find(l => l.id === objFormData.lotto_id)?.type === 'BIB-001M' ||
                          lotti.find(l => l.id === objFormData.lotto_id)?.type === 'BIB-002' ||
                          lotti.find(l => l.id === objFormData.lotto_id)?.type === 'BIB-003' ||
                          lotti.find(l => l.id === objFormData.lotto_id)?.type === 'BIB-008' ||
                          lotti.find(l => l.id === objFormData.lotto_id)?.type.startsWith('ARC-')
                        }
                        title={lotti.find(l => l.id === objFormData.lotto_id)?.type.startsWith('ARC-') ? "Autogenerato: ConservativeID_LogicalID" : ""}
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Logical ID</label>
                        <input type="text" name="logical_id" value={objFormData.logical_id || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Management ID</label>
                        <input type="text" name="management_id" value={objFormData.management_id || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Dossier ID</label>
                        <input
                          type="text"
                          name="dossier_id"
                          value={objFormData.dossier_id || ''}
                          onChange={handleObjInputChange}
                          disabled={lotti.find(l => l.id === objFormData.lotto_id)?.type === 'BIB-002'}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label>Titolo</label>
                    <input type="text" name="title" value={objFormData.title || ''} onChange={handleObjInputChange} />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Date Issued Start</label>
                        <input type="text" name="date_issued_start" value={objFormData.date_issued_start || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Date Issued End</label>
                        <input type="text" name="date_issued_end" value={objFormData.date_issued_end || ''} onChange={handleObjInputChange} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Data Pubblicazione</label>
                    <input type="text" name="publication_date" value={objFormData.publication_date || ''} onChange={handleObjInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Forma Fisica</label>
                    <input type="text" name="physical_form" value={objFormData.physical_form || ''} onChange={handleObjInputChange} />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Manoscritto Form</label>
                        <input type="text" name="manoscritto_form" value={objFormData.manoscritto_form || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Manoscritto Extent</label>
                        <input type="text" name="manoscritto_extent" value={objFormData.manoscritto_extent || ''} onChange={handleObjInputChange} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Access Condition</label>
                        <input type="text" name="access_condition" value={objFormData.access_condition || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Collezione</label>
                        <input type="text" name="collezione" value={objFormData.collezione || ''} onChange={handleObjInputChange} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico part-data</label>
                        <input type="text" name="periodical_part_data" value={objFormData.periodical_part_data || ''} onChange={handleObjInputChange} disabled />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico part-detail-number</label>
                        <input type="text" name="periodical_part_detail_number" value={objFormData.periodical_part_detail_number || ''} onChange={handleObjInputChange} disabled />
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Anno</label>
                        <input type="text" name="periodical_year" value={objFormData.periodical_year || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Mese</label>
                        <input type="text" name="periodical_month" value={objFormData.periodical_month || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Giorno</label>
                        <input type="text" name="periodical_day" value={objFormData.periodical_day || ''} onChange={handleObjInputChange} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Annata</label>
                        <input type="text" name="periodical_vol" value={objFormData.periodical_vol || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Numero</label>
                        <input type="text" name="periodical_issue" value={objFormData.periodical_issue || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Supplemento/Indice</label>
                        <select name="periodical_type" value={objFormData.periodical_type || 'Nessuno'} onChange={handleObjInputChange}>
                          <option value="Nessuno">Nessuno</option>
                          <option value="Supplemento">Supplemento</option>
                          <option value="Indice">Indice</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Anno Fine</label>
                        <input type="text" name="periodical_year_end" value={objFormData.periodical_year_end || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Mese Fine</label>
                        <input type="text" name="periodical_month_end" value={objFormData.periodical_month_end || ''} onChange={handleObjInputChange} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Annata Fine</label>
                        <input type="text" name="periodical_vol_end" value={objFormData.periodical_vol_end || ''} onChange={handleObjInputChange} />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Periodico Numero Fine</label>
                        <input type="text" name="periodical_issue_end" value={objFormData.periodical_issue_end || ''} onChange={handleObjInputChange} />
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button type="submit" className="btn btn-primary">Salva Oggetto</button>
                    <button type="button" className="btn btn-text" onClick={() => setEditingObject(null)}>Annulla</button>
                  </div>
                </form>
              </>
            )}
          </div>
        )
      }

      {
        activeTab === 'packages' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Pacchetti Generati (Packaged Repository)</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={handleDownloadPackagesReportExcel}
                  style={{ backgroundColor: '#4caf50' }}
                >
                  <i className="fas fa-file-excel"></i> REPORT EXCEL
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setIsSentinelModalOpen(true)}
                  style={{ backgroundColor: '#2196f3' }}
                >
                  <i className="fas fa-file-import"></i> CARICA LOG
                </button>
                <input
                  type="file"
                  ref={sentinelFileInputRef}
                  style={{ display: 'none' }}
                  accept=".csv"
                  onChange={handleSentinelUpload}
                />
                {cantiere.can_edit && <button className="btn btn-primary" onClick={handleSyncPackages}>Sincronizza</button>}
                {cantiere.can_edit && (
                  <button 
                    className="btn btn-primary" 
                    onClick={handleReconstructPackages}
                    style={{ backgroundColor: '#8a2be2' }}
                  >
                    <i className="fas fa-database"></i> RICOSTRUISCI DB
                  </button>
                )}
              </div>
            </div>
            <table className="table-responsive">
              <thead>
                <tr>
                  <th>Nome Pacchetto</th>
                  <th className="desktop-only">Indice</th>
                  <th className="desktop-only">Oggetto Digitale</th>
                  <th>Dimensione ZIP</th>
                  <th>Azioni</th>
                </tr>
                <tr>
                  <th></th>
                  <th className="desktop-only"></th>
                  <th className="desktop-only">
                    <input
                      type="text"
                      placeholder="Filtra oggetto..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={pkgFilters.directory_name}
                      onChange={(e) => { setPkgFilters({ ...pkgFilters, directory_name: e.target.value }); setPkgPage(1); }}
                    />
                  </th>
                  <th>
                    <input
                      type="number"
                      placeholder="Max MB..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={pkgFilters.max_size_mb}
                      onChange={(e) => { setPkgFilters({ ...pkgFilters, max_size_mb: e.target.value }); setPkgPage(1); }}
                    />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredPackages.slice((pkgPage - 1) * ITEMS_PER_PAGE, pkgPage * ITEMS_PER_PAGE).map((pkg) => (
                  <tr key={pkg.id} style={pkg.row_color ? { backgroundColor: pkg.row_color } : {}}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{pkg.name}</strong>
                        {pkg.is_prototype && (
                          <span style={{
                            fontSize: '0.6rem',
                            background: 'var(--error-color)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                          }}>
                            Prototipo
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>
                        {pkg.spedito_at && (
                          <div title={`Inviato a: ${pkg.spedito_to || 'N/A'}`}>
                            <i className="fas fa-paper-plane" style={{ marginRight: '4px' }}></i>
                            {new Date(pkg.spedito_at).toLocaleString('it-IT')} 
                            {pkg.spedito_to && ` (${pkg.spedito_to})`}
                          </div>
                        )}
                        {pkg.archiviato_at && (
                          <div style={{ color: '#2e7d32', marginTop: '2px' }}>
                            <i className="fas fa-check-double" style={{ marginRight: '4px' }}></i>
                            Archiviato: {new Date(pkg.archiviato_at).toLocaleString('it-IT')}
                          </div>
                        )}
                        {pkg.last_status_message && (
                          <div style={{ color: 'var(--primary-color)', fontWeight: 'bold', marginTop: '2px' }}>
                            <i className="fas fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>
                            {pkg.last_status_message}
                          </div>
                        )}
                      </div>
                      <div className="mobile-only" style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                        <i className="fas fa-folder-open" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>
                        {pkg.directory_name}
                      </div>
                    </td>
                    <td className="desktop-only">{pkg.index}</td>
                    <td className="desktop-only">{pkg.directory_name}</td>
                    <td>{pkg.zip_size || 'N/A'}</td>
                    <td>
                      <ActionMenu actions={
                        cantiere.can_edit ? [
                          { label: isShipping === pkg.id ? 'Invio...' : 'Invia a DPAC', icon: 'fas fa-upload', onClick: () => handleShipPackage(pkg), disabled: pkg.in_spedizione || isShipping !== null },
                          { label: 'Download ZIP', icon: 'fas fa-download', onClick: () => handleDownloadPackage(pkg) },
                          { label: 'Visualizza XML', icon: 'fas fa-file-code', onClick: () => handleViewXml(pkg) },
                          { label: 'Copia Link Pubblico', icon: 'fas fa-link', onClick: () => handleCopyLink(pkg.name) },
                          { label: 'Esplora', icon: 'fas fa-magnifying-glass', onClick: () => handleExplorePackage(pkg) },
                          { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => handleEditPackage(pkg) },
                          ...(pkg.trasferito ? [{ label: 'Reset Trasferimento', icon: 'fas fa-undo', onClick: () => handleResetTransfer(pkg) }] : []),
                          { label: 'Elimina', icon: 'fas fa-trash-can', onClick: () => handleDeletePackage(pkg), color: 'var(--error-color)' },
                        ] : [
                          { label: 'Download ZIP', icon: 'fas fa-download', onClick: () => handleDownloadPackage(pkg) },
                          { label: 'Visualizza XML', icon: 'fas fa-file-code', onClick: () => handleViewXml(pkg) },
                          { label: 'Copia Link Pubblico', icon: 'fas fa-link', onClick: () => handleCopyLink(pkg.name) },
                          { label: 'Esplora', icon: 'fas fa-magnifying-glass', onClick: () => handleExplorePackage(pkg) },
                        ]
                      } />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredPackages.length > ITEMS_PER_PAGE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center', marginTop: '20px' }}>
                <button
                  className="btn btn-secondary"
                  disabled={pkgPage === 1}
                  onClick={() => setPkgPage(1)}
                >
                  Prima
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={pkgPage === 1}
                  onClick={() => setPkgPage(pkgPage - 1)}
                >
                  Prev
                </button>
                <span>Pagina {pkgPage} di {Math.ceil(filteredPackages.length / ITEMS_PER_PAGE)}</span>
                <button
                  className="btn btn-secondary"
                  disabled={pkgPage === Math.ceil(filteredPackages.length / ITEMS_PER_PAGE)}
                  onClick={() => setPkgPage(pkgPage + 1)}
                >
                  Next
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={pkgPage === Math.ceil(filteredPackages.length / ITEMS_PER_PAGE)}
                  onClick={() => setPkgPage(Math.ceil(filteredPackages.length / ITEMS_PER_PAGE))}
                >
                  Ultima
                </button>
              </div>
            )}
            {filteredPackages.length === 0 && packages.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                Nessun pacchetto corrisponde ai filtri impostati.
              </div>
            )}
            {packages.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'rgba(0,0,0,0.4)' }}>Nessun pacchetto trovato.</p>}
          </div>
        )
      }

      {
        activeTab === 'lotti' && (
          <div className="card">
            <h3 style={{ marginBottom: '20px' }}>Lotti di Digitalizzazione</h3>
            <table className="table-responsive">
              <thead>
                <tr>
                  <th>Nome Lotto</th>
                  <th>Tipo</th>
                  <th>Client DPAC</th>
                  <th>Pacchetti Previsti</th>
                  <th>Oggetti allocati</th>
                  <th>Pacchettizzati</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {lotti.map((lotto) => (
                  <tr key={lotto.id}>
                    <td><strong>{lotto.name}</strong></td>
                    <td>{lotto.type}</td>
                    <td><span style={{ fontSize: '0.85rem', color: 'var(--primary-color)' }}>{lotto.dpac_client_name}</span></td>
                    <td>{lotto.expected_packages}</td>
                    <td>{lotto.allocated_packages || 0}</td>
                    <td>{lotto.packaged_count || 0}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        background: lotto.status === 'Completo' ? '#e8f5e9' : '#fff3e0',
                        color: lotto.status === 'Completo' ? '#2e7d32' : '#ef6c00'
                      }}>
                        {lotto.status}
                      </span>
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
            {lotti.length === 0 && <p style={{ textAlign: 'center', padding: '20px', color: 'rgba(0,0,0,0.4)' }}>Nessun lotto definito per questo cantiere.</p>}
          </div>
        )
      }

      {
        activeTab === 'config' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Metadati del Cantiere</h3>
              <button className="btn btn-primary" onClick={handleDownloadConfigExcel}>SCARICA EXCEL</button>
            </div>
            <form onSubmit={handleSaveConfig}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Creator</label>
                  <input type="text" name="creator" value={formData.creator} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>IPower 1</label>
                  <input type="text" name="ipower1" value={formData.ipower1} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>IPower 2</label>
                  <input type="text" name="ipower2" value={formData.ipower2} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Custodian</label>
                  <input type="text" name="custodian" value={formData.custodian} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Conservative ID</label>
                  <input type="text" name="conservative_id" value={formData.conservative_id} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Conservative ID Authority</label>
                  <input type="text" name="conservative_id_authority" value={formData.conservative_id_authority} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Relation ID</label>
                  <input type="text" name="relation_id" value={formData.relation_id} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Collection</label>
                  <input type="text" name="collection" value={formData.collection} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Publisher</label>
                  <input type="text" name="publisher" value={formData.publisher} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Produttore</label>
                  <input type="text" name="producer" value={formData.producer} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Luogo di pubblicazione</label>
                  <input type="text" name="publication_place" value={formData.publication_place} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Codice DPAC</label>
                  <input type="text" name="dpac_code" value={formData.dpac_code} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Codice Regione</label>
                  <input type="text" name="region_code" value={formData.region_code} onChange={handleInputChange} />
                </div>
              </div>
              {cantiere.can_edit && <button type="submit" className="btn btn-primary" style={{ marginTop: '10px' }}>Salva Configurazione</button>}
            </form>
          </div>
        )
      }
      {
        isImportModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '800px' }}>
              <h2>Importa Oggetti da Excel</h2>
              <p style={{ marginBottom: '15px' }}>
                Il file Excel deve seguire un formato specifico.
                Puoi scaricare il template qui: <button className="btn btn-link" style={{ padding: 0, verticalAlign: 'baseline' }} onClick={downloadTemplate}>Scarica Template</button>
              </p>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                <strong>Nota:</strong> Le cartelle elencate nel file devono esistere fisicamente nel repository del cantiere.
                I campi vuoti in Excel non sovrascriveranno i dati esistenti nel database.
              </p>

              <div className="form-group">
                <label>Seleziona file Excel (.xlsx)</label>
                <input type="file" accept=".xlsx" onChange={handleFileChange} />
              </div>

              {importLoading && <p>Elaborazione in corso...</p>}
              {importMessage && <p style={{ color: 'var(--error-color)' }}>{importMessage}</p>}

              {importPreview.length > 0 && (
                <div style={{ marginTop: '20px', maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
                  <table className="table-responsive" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                      <tr>
                        <th>Directory</th>
                        <th>Lotto</th>
                        <th>Status</th>
                        <th>Titolo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.directory_name}</td>
                          <td>{item.lotto}</td>
                          <td>
                            {item.status === 'new' && <span style={{ color: 'green' }}>Nuovo</span>}
                            {item.status === 'update' && <span style={{ color: 'blue' }}>Aggiornamento</span>}
                            {item.status === 'error' && <span style={{ color: 'red' }}>Errore: {item.message}</span>}
                          </td>
                          <td>{item.title}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleCommitImport}
                  disabled={importLoading || importPreview.length === 0 || importPreview.every(i => i.status === 'error')}
                >
                  Carica
                </button>
                <button className="btn btn-text" onClick={() => { setIsImportModalOpen(false); setImportPreview([]); setImportFile(null); }}>Annulla</button>
              </div>
            </div>
          </div>
        )
      }

      {isExploreModalOpen && exploringPackage && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h3>Contenuto Pacchetto: {exploringPackage.name}</h3>
              <button className="btn-close" onClick={() => setIsExploreModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <table className="table-responsive">
                <thead>
                  <tr>
                    <th>Nome File</th>
                    <th>Tipo</th>
                    <th>Dimensione</th>
                  </tr>
                </thead>
                <tbody>
                  {packageContents.length === 0 && (
                    <tr><td colSpan={3} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>Cartella vuota o in caricamento...</td></tr>
                  )}
                  {packageContents.map((file, i) => (
                    <tr key={i}>
                      <td>{file.is_dir ? <><i className="fas fa-folder" style={{ marginRight: '8px', color: '#ffa000' }}></i><strong>{file.name}/</strong></> : <><i className="fas fa-file" style={{ marginRight: '8px', color: '#90a4ae' }}></i>{file.name}</>}</td>
                      <td>{file.is_dir ? 'Directory' : 'File'}</td>
                      <td>{file.size !== null ? (file.size / 1024).toFixed(1) + ' KB' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer" style={{ textAlign: 'right', marginTop: '20px' }}>
              <button className="btn btn-primary" onClick={() => setIsExploreModalOpen(false)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Single Package Generation */}
      {isPkgModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Generazione Pacchetto: {pkgModalObj?.directory_name}</h3>
              <button className="btn-close" onClick={() => { setIsPkgModalOpen(false); setPkgModalObj(null); setPkgModalDirName(''); }} disabled={isPkgModalLoading}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 0' }}>
              {isPkgModalLoading && !pkgModalDirName ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="spinner-small" style={{ display: 'inline-block', marginBottom: '10px' }}></div>
                  <p>Recupero nome pacchetto consigliato...</p>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="package-dir-name" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Nome directory del pacchetto:
                  </label>
                  <input
                    id="package-dir-name"
                    type="text"
                    className="form-control"
                    value={pkgModalDirName}
                    onChange={(e) => setPkgModalDirName(e.target.value)}
                    style={{ width: '100%', padding: '10px', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    disabled={isPkgModalLoading}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
              <button
                className="btn btn-text"
                onClick={() => {
                  setIsPkgModalOpen(false);
                  setPkgModalObj(null);
                  setPkgModalDirName('');
                }}
                disabled={isPkgModalLoading}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmGeneratePackage}
                disabled={isPkgModalLoading || !pkgModalDirName.trim()}
              >
                {isPkgModalLoading ? 'Avvio in corso...' : 'Conferma Generazione'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLabelUploadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '900px', width: '95%' }}>
            <div className="modal-header">
              <h3>Caricamento Massivo Label (ZIP)</h3>
              <button className="btn-close" onClick={handleCloseLabelUpload}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {isLabelUploading && !labelUploadId && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '16px', color: 'var(--primary-color)' }}></i>
                  <p>Estrazione e analisi dello ZIP in corso...</p>
                </div>
              )}

              {labelUploadId && (!labelUploadStatus || labelUploadStatus.status === 'idle') && (
                <>
                  <p>Sono stati identificati <strong>{labelUploadItems.length}</strong> file CSV nell'archivio.</p>
                  <table className="table-responsive" style={{ marginTop: '16px' }}>
                    <thead>
                      <tr>
                        <th>File CSV</th>
                        <th>Corrispondenza Oggetto</th>
                        <th>File trovati</th>
                        <th>Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labelUploadItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.csv_filename}</td>
                          <td><strong>{item.object_name}</strong></td>
                          <td>{item.rows}</td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              background: item.status === 'found' ? '#e8f5e9' : '#ffebee',
                              color: item.status === 'found' ? '#2e7d32' : '#c62828'
                            }}>
                              {item.status === 'found' ? 'TROVATO' : 'NON TROVATO'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button className="btn btn-text" onClick={handleCloseLabelUpload}>Annulla</button>
                    <button
                      className="btn btn-primary"
                      onClick={handleApplyLabels}
                      disabled={isLabelUploading || labelUploadItems.filter(it => it.status === 'found').length === 0}
                    >
                      {isLabelUploading ? 'AVVIO...' : 'APPLICA LABEL IDENTIFICATE'}
                    </button>
                  </div>
                </>
              )}

              {labelUploadStatus && labelUploadStatus.status === 'running' && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '16px', color: 'var(--primary-color)' }}></i>
                  <h4>Applicazione in corso...</h4>
                  <div className="progress-bar" style={{ marginTop: '16px', height: '20px', background: '#eee', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(labelUploadStatus.processed / labelUploadStatus.total) * 100}%`,
                      height: '100%',
                      background: 'var(--primary-color)',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <p style={{ marginTop: '8px' }}>
                    Elaborazione oggetto {labelUploadStatus.processed} di {labelUploadStatus.total}
                  </p>
                </div>
              )}

              {labelUploadStatus && labelUploadStatus.status === 'completed' && (
                <div style={{ padding: '20px' }}>
                  <div style={{
                    background: labelUploadStatus.errors > 0 ? '#fff3e0' : '#e8f5e9',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{ margin: 0, color: labelUploadStatus.errors > 0 ? '#ef6c00' : '#2e7d32' }}>
                      {labelUploadStatus.errors > 0 ? 'Completato con errori' : 'Completato con successo'}
                    </h4>
                    <p style={{ margin: '8px 0 0 0' }}>
                      Oggetti aggiornati: {labelUploadStatus.success} | Errori: {labelUploadStatus.errors}
                    </p>
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem', background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                    {labelUploadStatus.details.map((d: string, i: number) => <div key={i}>{d}</div>)}
                  </div>
                  <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={handleCloseLabelUpload}>Chiudi</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {
        editingPackage && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Modifica Pacchetto</h3>
              <form onSubmit={handleSavePackage}>
                <div className="form-group">
                  <label>Nome Pacchetto (rinomina cartella fisica)</label>
                  <input
                    type="text"
                    value={pkgFormData.name}
                    onChange={(e) => setPkgFormData({ ...pkgFormData, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Indice</label>
                  <input
                    type="number"
                    value={pkgFormData.index}
                    onChange={(e) => setPkgFormData({ ...pkgFormData, index: parseInt(e.target.value) })}
                  />
                </div>
                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary">Salva</button>
                  <button type="button" className="btn btn-text" onClick={() => setEditingPackage(null)}>Annulla</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {
        isXmlModalOpen && (
          <div className="modal-overlay" onClick={() => setIsXmlModalOpen(false)}>
            <div className="modal-content" style={{ maxWidth: '1120px', width: '90%' }} onClick={e => e.stopPropagation()}>
              <h3>Visualizzatore XML: {xmlFilename || (xmlLoading ? 'Caricamento...' : 'Nessun file')}</h3>
              <div style={{ margin: '20px 0', maxHeight: '500px', overflowY: 'auto', border: '1px solid #ccc', padding: '10px', borderRadius: '4px', background: '#f8f9fa' }}>
                {xmlLoading ? (
                  <p>Caricamento in corso...</p>
                ) : (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontSize: '0.9rem' }}>
                    {xmlContent}
                  </pre>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <button className="btn btn-primary" onClick={() => setIsXmlModalOpen(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        )
      }
      {
        showPrepSummary && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>
                  {prepStatus?.status === 'running' ? 'Preparazione in Corso...' : 'Riepilogo Preparazione'}
                </h2>
                {prepStatus?.status === 'running' && <div className="spinner-small" />}
              </div>

              {prepStatus ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                    <div className="card" style={{ flex: 1, padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Oggetti Processati</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {prepStatus.processed} / {prepStatus.total}
                      </div>
                      {prepStatus.status === 'running' && (
                        <div style={{ height: '4px', background: '#eee', borderRadius: '2px', marginTop: '8px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              background: 'var(--primary-color)',
                              width: `${(prepStatus.processed / prepStatus.total) * 100}%`,
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="card" style={{ flex: 1, padding: '12px', textAlign: 'center', color: (prepStatus.errors?.length || 0) > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Errori Riscontrati</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{prepStatus.errors?.length || 0}</div>
                    </div>
                  </div>

                  {(prepStatus.errors?.length || 0) > 0 && (
                    <div style={{ maxHeight: '200px', overflow: 'auto', background: '#fff5f5', padding: '12px', borderRadius: '4px', border: '1px solid #fed7d7' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: 'var(--error-color)' }}>Dettaglio Errori:</h4>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.8rem' }}>
                        {prepStatus.errors.map((err: any, idx: number) => {
                          // Handle both string errors and object errors {error: string, object: string}
                          const errorMessage = typeof err === 'string' ? err : `${err.object || 'Unknown'}: ${err.error || JSON.stringify(err)}`;
                          return <li key={idx} style={{ marginBottom: '4px' }}>{errorMessage}</li>;
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Inizializzazione processo...
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {(prepStatus?.errors?.length || 0) > 0 && (
                  <button className="btn btn-outline" onClick={handleDownloadErrors}>Scarica Errori</button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => setShowPrepSummary(false)}
                >
                  {prepStatus?.status === 'running' ? 'Nascondi' : 'Chiudi'}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        showLabelSummary && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ margin: 0 }}>
                  {labelStatus?.status === 'running' ? 'Generazione Label in Corso...' : 'Riepilogo Generazione Label'}
                </h2>
                {labelStatus?.status === 'running' && <div className="spinner-small" />}
              </div>

              <div style={{ 
                  fontSize: '0.85rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: '20px',
                  padding: '6px 12px',
                  background: 'rgba(99,102,241, 0.1)',
                  borderRadius: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: 500
              }}>
                  <i className="fas fa-code-branch" style={{ marginRight: '8px' }}></i>
                  Algoritmo: <strong style={{ marginLeft: '4px' }}>{cantiere.algorithm_label_name || 'Genera Label (Default)'}</strong>
              </div>

              {labelStatus ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                    <div className="card" style={{ flex: 1, padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Oggetti Analizzati</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {labelStatus.processed} / {labelStatus.total}
                      </div>
                      {labelStatus.status === 'running' && (
                        <div style={{ height: '4px', background: '#eee', borderRadius: '2px', marginTop: '8px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              background: 'var(--primary-color)',
                              width: `${(labelStatus.processed / labelStatus.total) * 100}%`,
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="card" style={{ flex: 1, padding: '12px', textAlign: 'center', color: labelStatus.errors?.length > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Oggetti con Errori/Avvisi</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{labelStatus.errors?.length || 0}</div>
                    </div>
                  </div>

                  {labelStatus.errors?.length > 0 && (
                    <div style={{ maxHeight: '300px', overflow: 'auto', background: '#fff5f5', padding: '12px', borderRadius: '4px', border: '1px solid #fed7d7' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: 'var(--error-color)' }}>Dettaglio Avvisi (label.err):</h4>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.8rem' }}>
                        {labelStatus.errors.map((err: any, idx: number) => (
                          <li key={idx} style={{ marginBottom: '8px', borderBottom: '1px solid #feb2b2', paddingBottom: '4px' }}>
                            <strong>{err.object}</strong>:
                            <pre style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{err.content}</pre>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Inizializzazione processo...
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {labelStatus?.errors?.length > 0 && (
                  <button className="btn btn-outline" onClick={handleDownloadLabelErrors}>Scarica Report Errori</button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => setShowLabelSummary(false)}
                >
                  {labelStatus?.status === 'running' ? 'Nascondi' : 'Chiudi'}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        showDownloadLabelsModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ margin: 0 }}>
                  {downloadLabelsStatus?.status === 'running' ? 'Esportazione Label in Corso...' : 
                   downloadLabelsStatus?.status === 'failed' ? 'Esportazione Fallita' : 'Riepilogo Esportazione Label'}
                </h2>
                {downloadLabelsStatus?.status === 'running' && <div className="spinner-small" />}
              </div>

              {downloadLabelsStatus ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                    <div className="card" style={{ flex: 1, padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Oggetti Processati</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {downloadLabelsStatus.processed} / {downloadLabelsStatus.total}
                      </div>
                      {downloadLabelsStatus.status === 'running' && downloadLabelsStatus.total > 0 && (
                        <div style={{ height: '4px', background: '#eee', borderRadius: '2px', marginTop: '8px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              background: 'var(--primary-color)',
                              width: `${(downloadLabelsStatus.processed / downloadLabelsStatus.total) * 100}%`,
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {downloadLabelsStatus.status === 'failed' && (
                    <div style={{ padding: '12px', background: '#fff5f5', borderRadius: '4px', border: '1px solid #fed7d7', color: 'var(--error-color)', fontSize: '0.9rem', marginBottom: '16px' }}>
                      <strong>Errore:</strong> {downloadLabelsStatus.error || 'Errore imprevisto durante l\'elaborazione'}
                    </div>
                  )}

                  {downloadLabelsStatus.errors?.length > 0 && (
                    <div style={{ maxHeight: '200px', overflow: 'auto', background: '#fff5f5', padding: '12px', borderRadius: '4px', border: '1px solid #fed7d7' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: 'var(--error-color)' }}>Errori di compilazione:</h4>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.8rem' }}>
                        {downloadLabelsStatus.errors.map((err: any, idx: number) => (
                          <li key={idx} style={{ marginBottom: '4px' }}>
                            <strong>{err.object}</strong>: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Inizializzazione processo...
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {downloadLabelsStatus?.status === 'completed' && (
                  <button
                    className="btn btn-outline"
                    onClick={handleDownloadZipFile}
                    style={{ backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50' }}
                  >
                    Scarica ZIP
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => setShowDownloadLabelsModal(false)}
                >
                  {downloadLabelsStatus?.status === 'running' ? 'Nascondi' : 'Chiudi'}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        showXmlSummary && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '700px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>
                  {xmlStatus?.status === 'running' ? 'Generazione XML in Corso...' : 'Riepilogo Generazione XML'}
                </h2>
                {xmlStatus?.status === 'running' && <div className="spinner-small" />}
              </div>

              {xmlStatus ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                    <div className="card" style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Processati</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {xmlStatus.processed} / {xmlStatus.total}
                      </div>
                    </div>
                    <div className="card" style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>XML Creati</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>{xmlStatus.xml_created}</div>
                    </div>
                    <div className="card" style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Validati OK</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--success-color)' }}>{xmlStatus.valid}</div>
                    </div>
                    <div className="card" style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Errori</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: xmlStatus.errors?.length > 0 ? 'var(--error-color)' : '#ccc' }}>
                        {xmlStatus.errors?.length || 0}
                      </div>
                    </div>
                  </div>

                  {xmlStatus.status === 'running' && (
                    <div style={{ height: '4px', background: '#eee', borderRadius: '2px', marginBottom: '16px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          background: 'var(--primary-color)',
                          width: `${(xmlStatus.processed / (xmlStatus.total || 1)) * 100}%`,
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                  )}

                  {xmlStatus.errors?.length > 0 && (
                    <div style={{ maxHeight: '300px', overflow: 'auto', background: '#fff5f5', padding: '12px', borderRadius: '4px', border: '1px solid #fed7d7' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: 'var(--error-color)' }}>Dettaglio Errori:</h4>
                      <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ textAlign: 'left' }}>
                            <th style={{ padding: '4px' }}>Oggetto</th>
                            <th style={{ padding: '4px' }}>Tipo</th>
                            <th style={{ padding: '4px' }}>Dettaglio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {xmlStatus.errors.map((err: any, idx: number) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #feb2b2' }}>
                              <td style={{ padding: '4px', fontWeight: 'bold' }}>{err.object}</td>
                              <td style={{ padding: '4px' }}>{err.type}</td>
                              <td style={{ padding: '4px', fontFamily: 'monospace' }}>
                                {typeof err.content === 'object' ? JSON.stringify(err.content, null, 2) : err.content}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Inizializzazione processo...
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {xmlStatus?.errors?.length > 0 && (
                  <button className="btn btn-outline" onClick={handleDownloadXmlErrors}>Scarica CSV Errori</button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => setShowXmlSummary(false)}
                >
                  {xmlStatus?.status === 'running' ? 'Nascondi' : 'Chiudi'}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        showTypeSelectionModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <h2 style={{ marginTop: 0 }}>Generazione Label Massiva</h2>
              
              <div style={{ marginBottom: '16px', padding: '12px', background: '#e0f2f1', borderRadius: '6px', border: '1px solid #b2dfdb' }}>
                <span style={{ fontSize: '0.85rem', color: '#004d40', display: 'block', fontWeight: 'bold', marginBottom: '2px' }}>
                  Algoritmo Generazione Label configurato:
                </span>
                <span style={{ fontSize: '0.95rem', color: '#00796b', fontFamily: 'monospace', wordBreak: 'break-all', fontWeight: '600' }}>
                  {cantiere.algorithm_label_name || 'Genera Label (Default)'}
                </span>
              </div>

              <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px', lineHeight: '1.4' }}>
                L'operazione avvierà la generazione delle label per gli oggetti digitali di questo cantiere che <strong>non</strong> sono in stato <strong>Completo</strong>, <strong>Collaudato</strong> o <strong>Pacchettizzato</strong>.
              </p>

              <div className="form-group" style={{ marginTop: '16px', marginBottom: '16px' }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '0.9rem' }}>
                  Oggetti da elaborare:
                </label>
                <div style={{ display: 'flex', gap: '16px', fontSize: '0.9rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="labelBatchScope"
                      value="all"
                      checked={batchScope === 'all'}
                      onChange={() => setBatchScope('all')}
                    />
                    Tutti gli oggetti
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="labelBatchScope"
                      value="filtered"
                      checked={batchScope === 'filtered'}
                      onChange={() => setBatchScope('filtered')}
                    />
                    Solo oggetti filtrati ({filteredObjects.length})
                  </label>
                </div>
              </div>

              {stats && stats.status_counts && stats.status_counts['Non Lottizzato'] > 0 ? (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '0.9rem' }}>
                    Tipo Oggetto per oggetti "Non Lottizzati":
                  </label>
                  <p style={{ fontSize: '0.82rem', color: '#666', margin: '0 0 8px 0' }}>
                    Sono presenti oggetti senza lotto assegnato. Seleziona il tipo da associare per generare le label correttamente.
                  </p>
                  <select
                    className="form-control"
                    value={selectedDefaultType}
                    onChange={(e) => setSelectedDefaultType(e.target.value)}
                  >
                    {['BIB-001', 'BIB-001M', 'BIB-002', 'BIB-008', 'ARC-001', 'ARC-002', 'ARC-003', 'ARC-004', 'ARC-004_AS', 'ARC-005', 'ABAP-003', 'FOTO-001'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    checked={forceGenerateLabels}
                    onChange={(e) => setForceGenerateLabels(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Forza la rigenerazione delle label (sovrascrive anche le label degli oggetti Completi/Collaudati/Pacchettizzati)
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowTypeSelectionModal(false)}
                >
                  Annulla
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const defaultType = (stats && stats.status_counts && stats.status_counts['Non Lottizzato'] > 0) ? selectedDefaultType : null;
                    confirmGenerateLabels(defaultType);
                  }}
                >
                  Conferma e Genera
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        batchActionModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <h2 style={{ marginTop: 0 }}>
                {batchActionModal === 'exif' && 'Generazione EXIF Massiva'}
                {batchActionModal === 'xml' && 'Generazione XML Massiva'}
                {batchActionModal === 'packages' && 'Generazione Pacchetti Massiva'}
              </h2>
              
              <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '16px', lineHeight: '1.4' }}>
                Scegli l'ambito di applicazione per l'operazione di{' '}
                <strong>
                  {batchActionModal === 'exif' && 'generazione EXIF'}
                  {batchActionModal === 'xml' && 'generazione XML'}
                  {batchActionModal === 'packages' && 'generazione pacchetti'}
                </strong>:
              </p>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.95rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="batchScope"
                      value="all"
                      checked={batchScope === 'all'}
                      onChange={() => setBatchScope('all')}
                    />
                    Tutti gli oggetti del cantiere
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="batchScope"
                      value="filtered"
                      checked={batchScope === 'filtered'}
                      onChange={() => setBatchScope('filtered')}
                    />
                    Solo oggetti filtrati dalla ricerca ({filteredObjects.length})
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setBatchActionModal(null)}
                >
                  Annulla
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (batchActionModal === 'exif') confirmGenerateExif();
                    else if (batchActionModal === 'xml') confirmGenerateXml();
                    else if (batchActionModal === 'packages') confirmGeneratePackages();
                  }}
                >
                  Conferma e Avvia
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        isMonitoringOpen && (
          <MonitoringModal
            status={monitoringPkgStatus}
            onClose={() => setIsMonitoringOpen(false)}
          />
        )
      }
      {
        showStatsModal && stats && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: 0 }}>Statistiche Cantiere</h2>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '4px' }}>
                    Report del {stats.report_date || 'N/A'}
                  </div>
                </div>
                <button className="btn btn-text" onClick={() => setShowStatsModal(false)} style={{ padding: '0 8px', fontSize: '1.2rem' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <strong>Cantiere:</strong> <span>{stats.cantiere_name || cantiere.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <strong>Codice DPAC:</strong> <span>{stats.dpac_code || cantiere.dpac_code}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <strong>Data e ora del report:</strong> <span>{stats.report_date || 'N/A'}</span>
                </div>

                <h3 style={{ margin: '10px 0 0 0', fontSize: '1.1em', color: '#555' }}>Risorse</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                    <span>Oggetti:</span> <strong>{stats.total_objects}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                    <span>Pacchetti:</span> <strong>{stats.total_packages}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                    <span>File TIFF:</span> <strong>{stats.total_tiff}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                    <span>File JPEG:</span> <strong>{stats.total_jpeg}</strong>
                  </div>
                </div>

                <h3 style={{ margin: '10px 0 0 0', fontSize: '1.1em', color: '#555' }}>Volumi Pacchetti</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <span>Volume complessivo:</span> <strong>{stats.vol_complessivo}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <span style={{ color: '#4caf50' }}>Volume trasferiti:</span> <strong>{stats.vol_trasferiti}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <span style={{ color: '#2196f3' }}>Volume in trasferimento:</span> <strong>{stats.vol_in_trasferimento}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                  <span style={{ color: '#ff9800' }}>Volume restanti:</span> <strong>{stats.vol_restanti}</strong>
                </div>
              </div>

              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={() => setShowStatsModal(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        )
      }
      {
        showPkgGenSummary && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>
                  {pkgGenStatus?.status === 'running' ? 'Generazione Pacchetti in Corso...' : 'Riepilogo Generazione Pacchetti'}
                </h2>
                {pkgGenStatus?.status === 'running' && <div className="spinner-small" />}
              </div>

              {pkgGenStatus ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                    <div className="card" style={{ flex: 1, padding: '12px', textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>Pacchetti Creati</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {pkgGenStatus.processed} / {pkgGenStatus.total}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Inizializzazione processo...
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowPkgGenSummary(false)}
                >
                  {pkgGenStatus?.status === 'running' ? 'Nascondi' : 'Chiudi'}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {isSentinelModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Carica Log (CSV)</h3>
              <button className="btn-close" onClick={() => setIsSentinelModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.9rem', marginBottom: '20px' }}>
                Seleziona il file CSV esportato da Sentinel per aggiornare lo stato di archiviazione dei pacchetti.
              </p>
              
              <div 
                style={{ 
                  border: '2px dashed #ccc', 
                  padding: '30px', 
                  textAlign: 'center', 
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: '#f9f9f9',
                  transition: 'background 0.2s'
                }}
                onClick={() => sentinelFileInputRef.current?.click()}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
              >
                {isSentinelUploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin fa-2x" style={{ color: 'var(--primary-color)', marginBottom: '10px' }}></i>
                    <p>Elaborazione in corso...</p>
                  </>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt fa-3x" style={{ color: '#ccc', marginBottom: '10px' }}></i>
                    <p>Clicca per selezionare il file .csv</p>
                    <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '8px' }}>
                      Mappa nfname &rarr; Pacchetto se Sentinel State &egrave; COMPLETED
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ textAlign: 'right', marginTop: '20px' }}>
              <button className="btn btn-text" onClick={() => setIsSentinelModalOpen(false)} disabled={isSentinelUploading}>Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default CantiereBrowser;